import { useCallback, useState } from 'react';
import { isEmpty } from 'lodash';
import { SpreadsheetNotesService } from 'api';
import { RANGE_SENSITIVITY_REFERENCE_TYPE } from 'common/constants/documents';
import { ValuationByAllocation } from 'common/types/valuation';
import { NoteData } from 'components/Notes/types';
import { FileToSave } from 'services/hooks/useDocuments';
import { getArrayValue, getNumberValue, getStringValue } from 'utilities';
import { File, Note, SpreadsheetNote } from '../../types';

interface SaveNotesOptions {
  onSuccess?: (savedNotes: NoteData[]) => void;
  onSettled?: () => void;
}

interface UseRangeSensitivityDocumentsAndNotesParams {
  addReferenceForExistingDocuments: (
    measurementDateId: string | number,
    selectedValues: FileToSave[],
    referencedFeatureId: string | number,
    referenceType: string
  ) => Promise<void>;
  measurementDateId?: string | number | null;
  notesSaveInFlightByPanelRef: { current: Set<string> };
  saveNotes: (notesData?: NoteData[], options?: SaveNotesOptions) => void;
  valuation?: ValuationByAllocation | null;
}

export const useRangeSensitivityDocumentsAndNotes = (params: UseRangeSensitivityDocumentsAndNotesParams) => {
  const { addReferenceForExistingDocuments, measurementDateId, notesSaveInFlightByPanelRef, saveNotes, valuation } =
    params;
  const [filesInRangeSensitivity, setFilesInRangeSensitivity] = useState<File[]>([]);
  const [notesInRangeSensitivity, setNotesInRangeSensitivity] = useState<Note[]>([]);
  const [spreadsheetNotesInRangeSensitivity, setSpreadsheetNotesInRangeSensitivity] = useState<SpreadsheetNote[]>([]);

  const saveRangeSensitivityNotes = useCallback(() => {
    if (!isEmpty(notesInRangeSensitivity)) {
      notesInRangeSensitivity.forEach(noteEntry => {
        const panelId = getStringValue(noteEntry?.panelId);

        if (
          noteEntry?.notes &&
          !isEmpty(noteEntry?.notes) &&
          noteEntry?.notesHasChanged &&
          !notesSaveInFlightByPanelRef.current.has(panelId)
        ) {
          notesSaveInFlightByPanelRef.current.add(panelId);

          saveNotes(noteEntry?.notes, {
            onSuccess: savedNotes => {
              setNotesInRangeSensitivity(previousNotes =>
                previousNotes.map(currentNote =>
                  currentNote.panelId === noteEntry.panelId
                    ? {
                      ...currentNote,
                      notes: savedNotes,
                      notesHasChanged: false,
                    }
                    : currentNote
                )
              );
            },
            onSettled: () => {
              notesSaveInFlightByPanelRef.current.delete(panelId);
            },
          });
        }
      });
    }
  }, [notesInRangeSensitivity, notesSaveInFlightByPanelRef, saveNotes]);

  const saveRangeSensitivityDocuments = useCallback(async () => {
    if (isEmpty(filesInRangeSensitivity)) {
      return;
    }

    const rangeSensitivities = getArrayValue(valuation?.range_sensitivities);

    const filesReferences: Promise<void>[] = [];

    filesInRangeSensitivity.forEach(file => {
      const rangeSensitivity = rangeSensitivities.find(
        rs => getStringValue((rs as { panelId?: string }).panelId ?? rs.panel_id) === getStringValue(file?.panelId)
      );
      const referencedFeatureId = rangeSensitivity?.id ?? Number(file?.panelId);

      if (!referencedFeatureId || isEmpty(file?.filesToSave)) {
        return;
      }

      filesReferences.push(
        addReferenceForExistingDocuments(
          getNumberValue(measurementDateId),
          getArrayValue(file?.filesToSave) as unknown as FileToSave[],
          getNumberValue(referencedFeatureId),
          RANGE_SENSITIVITY_REFERENCE_TYPE
        )
      );
    });

    await Promise.all(filesReferences);

    setFilesInRangeSensitivity([]);
  }, [addReferenceForExistingDocuments, filesInRangeSensitivity, measurementDateId, valuation?.range_sensitivities]);

  const saveRangeSensitivitySpreadsheetNotes = useCallback(async () => {
    if (isEmpty(spreadsheetNotesInRangeSensitivity)) {
      return;
    }

    const entriesToSave = spreadsheetNotesInRangeSensitivity.filter(
      entry => entry?.hasChanged && !isEmpty(entry?.notes)
    );

    if (isEmpty(entriesToSave)) {
      return;
    }

    await Promise.all(
      entriesToSave.map(entry => SpreadsheetNotesService.apiSpreadsheetNotesCreate(getArrayValue(entry?.notes)))
    );

    setSpreadsheetNotesInRangeSensitivity(previousEntries =>
      previousEntries.map(entry => (entry?.hasChanged ? { ...entry, hasChanged: false } : entry))
    );
  }, [spreadsheetNotesInRangeSensitivity]);

  return {
    filesInRangeSensitivity,
    notesInRangeSensitivity,
    saveRangeSensitivityDocuments,
    saveRangeSensitivityNotes,
    saveRangeSensitivitySpreadsheetNotes,
    setFilesInRangeSensitivity,
    setNotesInRangeSensitivity,
    setSpreadsheetNotesInRangeSensitivity,
    spreadsheetNotesInRangeSensitivity,
  };
};
